
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'reference',
  source: 'endpoint',
  description: 'API reference for Reseller Partner endpoints and webhooks used to manage reseller partner end customers.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Partner endpoints and webhooks' subtitle='Create and manage end customers' mdxType="Header" />
    <p>{`For general, non-reference documentation, see `}<a parentName="p" {...{
        "href": "/docs/account/resellers/"
      }}>{`Reseller partners`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/partner/customer/create`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create an end customer`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/partner/customer/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get the status of an end customer`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/partner/customer/oauth_institutions/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get the OAuth-institution registration status for an end customer`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/partner/customer/enable`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Enable an end customer in Production`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/partner/customer/remove`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Remove an end customer`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Webhooks`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#end_customer_oauth_status_updated"
            }}><inlineCode parentName="a">{`END_CUSTOMER_OAUTH_STATUS_UPDATED`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Customer OAuth status updated`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "endpoints"
    }}>{`Endpoints`}</h3>
    <h4 {...{
      "id": "partnercustomercreate"
    }}>{`/partner/customer/create`}</h4>
    <Schema method='POST' route='/partner/customer/create' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/partner/customer/create' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: PartnerCustomerCreateRequest = {
  address: {
    city: city,
    country_code: countryCode,
    postal_code: postalCode,
    region: region,
    street: street,
  },
  application_name: applicationName,
  billing_contact: {
    email: billingEmail,
    given_name: billingGivenName,
    family_name: billingFamilyName,
  },
  customer_support_info: {
    email: supportEmail,
    phone_number: supportPhoneNumber,
    contact_url: supportContactUrl,
    link_update_url: linkUpdateUrl,
  },
  company_name: companyName,
  is_bank_addendum_completed: true,
  is_diligence_attested: true,
  legal_entity_name: legalEntityName,
  products: products,
  technical_contact: {
    email: technicalEmail,
    given_name: technicalGivenName,
    family_name: technicalFamilyName,
  },
  website: website,
};
try {
  const response = await plaidClient.partnerCustomerCreate(request);
  const endCustomer = response.data.end_customer;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/partner/customer/create \\
-H 'Content-Type: application/json' \\
-d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "address": {
        "city": String,
        "country_code": String,
        "postal_code": String,
        "region": String,
        "street": String
    },
    "application_name": String,
    "billing_contact": {
        "email": String,
        "given_name": String,
        "family_name": String
    },
    "customer_support_info": {
        "email": String,
        "phone_number": String,
        "contact_url": String,
        "link_update_url": String
    },
    "company_name": String,
    "is_bank_addendum_completed": Boolean,
    "is_diligence_attested": Boolean,
    "legal_entity_name": String,
    "products": [String],
    "technical_contact": {
        "email": String,
        "given_name": String,
        "family_name": String
    },
    "website": String
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::PartnerCustomerCreateRequest.new(
  {
      address: {
          city: city,
          country_code: country_code,
          postal_code: postal_code,
          region: region,
          street: street
      },
      application_name: application_name,
      billing_contact: {
          email: billing_email,
          given_name: billing_given_name,
          family_name: billing_family_name
      },
      customer_support_info: {
          email: support_email,
          phone_number: support_phone_number,
          contact_url: support_contact_url,
          link_update_url: link_update_url
      },
      company_name: company_name,
      is_bank_addendum_completed: true,
      is_diligence_attested: true,
      legal_entity_name: legal_entity_name,
      products: products,
      technical_contact: {
          email: technical_email,
          given_name: technical_given_name,
          family_name: technical_family_name
      },
      website: website
  }
)
response = client.partner_customer_create(request)
end_customer = response.end_customer
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`PartnerCustomerCreateRequest request = new PartnerCustomerCreateRequest()
  .address(address) // PartnerEndCustomerAddress
  .applicationName(applicationName) // String
  .billingContact(billingContact) // PartnerEndCustomerBillingContact
  .customerSupportInfo(customerSupportInfo) // PartnerEndCustomerCustomerSupportInfo
  .companyName(companyName) // String
  .isBankAddendumCompleted(true)
  .isDiligenceAttested(true)
  .legalEntityName(legalEntityName) // String
  .products(products) // List<Products>
  .technicalContact(technicalContact) // PartnerEndCustomerTechnicalContact
  .website(website); // String

Response<PartnerCustomerCreateResponse> response = client()
  .partnerCustomerCreate(request)
  .execute();
PartnerEndCustomerWithSecrets endCustomer = response.body().getEndCustomer();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = PartnerCustomerCreateRequest(
          address=address, # PartnerEndCustomerAddress
          application_name=application_name, # str
          billing_contact=billing_contact, # PartnerEndCustomerBillingContact
          customer_support_info=customer_support_info, # PartnerEndCustomerCustomerSupportInfo
          company_name=company_name, # str
          is_bank_addendum_completed=True,
          is_diligence_attested=True,
          legal_entity_name=legal_entity_name, # str
          products=products, # [Products]
          technical_contact=technical_contact, # PartnerEndCustomerTechnicalContact
          website=website #str
)
response = client.partner_customer_create(request)
end_customer = response['end_customer']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewPartnerCustomerCreateRequest(
    companyName, // string
    isDiligenceAttested, // boolean
    legalEntityName, // string
    website, // string
    applicationName, // string
    address, // PartnerEndCustomerAddress
    isBankAddendumCompleted, // boolean
)
request.SetProducts(products)
request.SetTechnicalContact(technicalContact)
request.SetBillingContact(billingContact)
request.SetCustomerSupportInfo(customerSupportInfo)
resp, _, err := client.PlaidApi.PartnerCustomerCreate(ctx).PartnerCustomerCreateRequest(*request).Execute()
if err != nil {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.PartnerCustomerCreateRequest{
    CompanyName:             companyName,             // string
    IsDiligenceAttested:     isDiligenceAttested,     // boolean
    LegalEntityName:         legalEntityName,         // string
    Website:                 website,                 // string
    ApplicationName:         applicationName,         // string
    Address:                 &address,                // PartnerEndCustomerAddress
    IsBankAddendumCompleted: isBankAddendumCompleted, // boolean
}
request.Products = products
request.TechnicalContact = &technicalContact
request.BillingContact = &billingContact
request.CustomerSupportInfo = &customerSupportInfo
resp, err := client.Partner.CreateCustomer(ctx, request)
if err != nil {
    // handle error
}
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/partner/customer/create' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "partnercustomerget"
    }}>{`/partner/customer/get`}</h4>
    <Schema method='POST' route='/partner/customer/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/partner/customer/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: PartnerCustomerGetRequest = {
  end_customer_client_id: clientId,
};
try {
  const response = await plaidClient.partnerCustomerGet(request);
  const endCustomer = response.data.end_customer;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/partner/customer/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "end_customer_client_id": String
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::PartnerCustomerGetRequest.new(
  {
    end_customer_client_id: client_id
  }
)
response = client.partner_customer_get(request)
end_customer = response.end_customer
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`PartnerCustomerGetRequest request = new PartnerCustomerGetRequest()
  .endCustomerClientId(clientId);
Response<PartnerCustomerGetResponse> response = client()
  .partnerCustomerGet(request)
  .execute();
PartnerEndCustomer endCustomer = response.body().getEndCustomer();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = PartnerCustomerGetRequest(
    end_customer_client_id=client_id,
)
response = client.partner_customer_get(request)
end_customer = response['end_customer']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewPartnerCustomerGetRequest(clientID)
resp, _, err := client.PlaidApi.PartnerCustomerGet(ctx).PartnerCustomerGetRequest(*request).Execute()
if err != nil {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.PartnerCustomerGetRequest{EndCustomerClientID: clientID}
resp, err := client.Partner.GetCustomer(ctx, request)
if err != nil {
    // handle error
}
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/partner/customer/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "partnercustomeroauth_institutionsget"
    }}>{`/partner/customer/oauth_institutions/get`}</h4>
    <Schema method='POST' route='/partner/customer/oauth_institutions/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/partner/customer/oauth_institutions/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: PartnerCustomerOAuthInstitutionsGetRequest = {
  end_customer_client_id: clientId,
};
try {
  const response = await plaidClient.partnerCustomerOauthInstitutionsGet(
    request,
  );
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/partner/customer/oauth_institutions/get \\
  -H 'Content-Type: application/json' \\
  -d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "end_customer_client_id": String
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::PartnerCustomerOAuthInstitutionsGetRequest.new(
  {
    end_customer_client_id: client_id
  }
)
response = client.partner_customer_oauth_institutions_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`PartnerCustomerOAuthInstitutionsGetRequest request = new PartnerCustomerOAuthInstitutionsGetRequest()
  .endCustomerClientId(clientId);
Response<PartnerCustomerOAuthInstitutionsGetResponse> response = client()
  .partnerCustomerOauthInstitutionsGet(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = PartnerCustomerOAuthInstitutionsGetRequest(
  end_customer_client_id=client_id,
)
response = client.partner_customer_oauth_institutions_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewPartnerCustomerOAuthInstitutionsGetRequest(clientID)
resp, _, err := client.PlaidApi.PartnerCustomerOauthInstitutionsGet(ctx).PartnerCustomerOAuthInstitutionsGetRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.PartnerCustomerOAuthInstitutionsGetRequest{EndCustomerClientID: clientID}
resp, err := client.Partner.GetCustomerOauthInstitutions(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/partner/customer/oauth_institutions/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "partnercustomerenable"
    }}>{`/partner/customer/enable`}</h4>
    <Schema method='POST' route='/partner/customer/enable' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/partner/customer/enable' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: PartnerCustomerEnableRequest = {
  end_customer_client_id: clientId,
};
try {
  const response = await plaidClient.partnerCustomerEnable(request);
  const productionSecret = response.data.production_secret;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/partner/customer/enable \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "end_customer_client_id": String
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::PartnerCustomerEnableRequest.new(
  {
    end_customer_client_id: client_id
  }
)
response = client.partner_customer_enable(request)
production_secret = response.production_secret
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`PartnerCustomerEnableRequest request = new PartnerCustomerEnableRequest()
  .endCustomerClientId(clientId);
Response<PartnerCustomerEnableResponse> response = client()
  .partnerCustomerEnable(request)
  .execute();
String productionSecret = response.body().getProductionSecret();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = PartnerCustomerEnableRequest(
    end_customer_client_id=client_id,
)
response = client.partner_customer_enable(request)
production_secret = response['production_secret']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewPartnerCustomerEnableRequest(clientID)
resp, _, err := client.PlaidApi.PartnerCustomerEnable(ctx).PartnerCustomerEnableRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.PartnerCustomerEnableRequest{EndCustomerClientID: clientID}
resp, err := client.Partner.EnableCustomer(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/partner/customer/enable' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "partnercustomerremove"
    }}>{`/partner/customer/remove`}</h4>
    <Schema method='POST' route='/partner/customer/remove' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/partner/customer/remove' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: PartnerCustomerRemoveRequest = {
  end_customer_client_id: clientId,
};
try {
  const response = await plaidClient.partnerCustomerRemove(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/partner/customer/remove \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "end_customer_client_id": String
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::PartnerCustomerRemoveRequest.new(
  {
    end_customer_client_id: client_id
  }
)
response = client.partner_customer_remove(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`PartnerCustomerRemoveRequest request = new PartnerCustomerRemoveRequest()
  .endCustomerClientId(clientId);
Response<PartnerCustomerRemoveResponse> response = client()
  .partnerCustomerRemove(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = PartnerCustomerRemoveRequest(
    end_customer_client_id=client_id,
)
response = client.partner_customer_remove(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewPartnerCustomerRemoveRequest(clientID)
resp, _, err := client.PlaidApi.PartnerCustomerRemove(ctx).PartnerCustomerRemoveRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.PartnerCustomerRemoveRequest{EndCustomerClientID: clientID}
resp, err := client.Partner.RemoveCustomer(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/partner/customer/remove' type='response' mdxType="Schema" />
    <h3 {...{
      "id": "webhooks"
    }}>{`Webhooks`}</h3>
    <h4 {...{
      "id": "end_customer_oauth_status_updated"
    }}><inlineCode parentName="h4">{`END_CUSTOMER_OAUTH_STATUS_UPDATED`}</inlineCode></h4>
    <Schema model='PartnerEndCustomerOAuthStatusUpdatedWebhook' type='model' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "endpoints",
        level: 3,
        title: "Endpoints",
      },
{
        id: "partnercustomercreate",
        level: 4,
        title: "/partner/customer/create",
      },
{
        id: "partnercustomerget",
        level: 4,
        title: "/partner/customer/get",
      },
{
        id: "partnercustomeroauth_institutionsget",
        level: 4,
        title: "/partner/customer/oauth_institutions/get",
      },
{
        id: "partnercustomerenable",
        level: 4,
        title: "/partner/customer/enable",
      },
{
        id: "partnercustomerremove",
        level: 4,
        title: "/partner/customer/remove",
      },
{
        id: "webhooks",
        level: 3,
        title: "Webhooks",
      },
{
        id: "end_customer_oauth_status_updated",
        level: 4,
        title: "END_CUSTOMER_OAUTH_STATUS_UPDATED",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
